import { useEffect, useRef, type RefObject } from "react";
import { Map as MapOlType } from "ol";

import type ImageLayer from "ol/layer/Image";
import type { ImageStatic } from "ol/source";

import { addGeoTiffLayer } from "../helpers";
import { GEOTIFF_IMAGE_SPEED } from "../map-constant";
import type { GeoController, IImageryState } from "../types/map-type";

type UseMapGeotiffProps = {
	mapRefOl: RefObject<MapOlType | null>;
	observationImagery?: IImageryState;
};

export const useMapGeotiff = ({
	mapRefOl,
	observationImagery,
}: UseMapGeotiffProps) => {
	const geotiffControllerRef = useRef<GeoController<
		ImageLayer<ImageStatic>
	> | null>(null);
	useEffect(() => {
		const map = mapRefOl.current;

		if (!map) return;

		if (
			!observationImagery?.geotiff ||
			observationImagery.geotiff.length === 0 ||
			!observationImagery.show
		) {
			geotiffControllerRef.current?.stop();

			geotiffControllerRef.current?.layers.forEach((layer) => {
				map.removeLayer(layer);
			});

			geotiffControllerRef.current = null;

			return;
		}

		const safeSpeed = Math.max(0.1, observationImagery.speed ?? 1);

		const baseUrl = `https://kuwait.exelys.io/tmp_raster/${observationImagery.imageryFilter}`;

		const controller = addGeoTiffLayer({
			geotiffFiles: observationImagery.geotiff,
			baseUrl,
			imagerySpeed: GEOTIFF_IMAGE_SPEED / safeSpeed,
			currentIndex: observationImagery.currentIndex ?? 0,
			setCurrentIndex: observationImagery.setCurrentIndexJson,
		});

		controller.layers.forEach((layer) => {
			map.addLayer(layer);
		});

		geotiffControllerRef.current = controller;

		if (observationImagery.isPlayed) {
			controller.start();
		} else {
			controller.stop();
		}

		return () => {
			controller.stop();

			controller.layers.forEach((layer) => {
				map.removeLayer(layer);
			});

			if (geotiffControllerRef.current === controller) {
				geotiffControllerRef.current = null;
			}
		};
	}, [
		mapRefOl,
		observationImagery?.imageryFilter,
		observationImagery?.geotiff,
		observationImagery?.show,
	]);

	useEffect(() => {
		const controller = geotiffControllerRef.current;

		if (!controller) return;

		const visible = !!observationImagery?.show;

		controller.layers.forEach((layer) => {
			layer.setVisible(visible);
		});
	}, [observationImagery?.show]);

	useEffect(() => {
		const controller = geotiffControllerRef.current;

		if (!controller) return;

		if (observationImagery?.isPlayed) {
			controller.start();
		} else {
			controller.stop();
		}
	}, [observationImagery?.isPlayed]);

	useEffect(() => {
		if (
			!observationImagery ||
			observationImagery.show ||
			!observationImagery.isPlayed
		) {
			return;
		}

		const safeSpeed = Math.max(0.1, observationImagery.speed);

		const totalSteps = observationImagery.geotiff?.length || 24;
		const intervalTime = GEOTIFF_IMAGE_SPEED / safeSpeed + 150;

		const interval = setInterval(() => {
			const currentIndex = observationImagery.currentIndex ?? 0;
			const nextIndex = (currentIndex + 1) % totalSteps;
			observationImagery.setCurrentIndexJson(nextIndex);
		}, intervalTime);

		return () => {
			clearInterval(interval);
		};
	}, [
		observationImagery?.show,
		observationImagery?.isPlayed,
		observationImagery?.currentIndex,
		observationImagery?.speed,
	]);

	/*
	 * Vitesse
	 */
	useEffect(() => {
		const controller = geotiffControllerRef.current;

		if (!controller) return;

		const safeSpeed = Math.max(0.1, observationImagery?.speed ?? 1);

		controller.setSpeed(GEOTIFF_IMAGE_SPEED / safeSpeed);
	}, [observationImagery?.speed]);

	/*
	 * Reset de l'index
	 */
	useEffect(() => {
		if (!observationImagery) return;

		observationImagery.setCurrentIndexJson(0);
	}, [observationImagery?.imageryFilter]);

	/*
	 * Synchronisation de l'index externe
	 */
	useEffect(() => {
		const controller = geotiffControllerRef.current;
		const index = observationImagery?.currentIndex;

		if (!controller || index == null) return;

		controller.seek(index);
	}, [observationImagery?.currentIndex]);

	useEffect(() => {
		// si pas d imagery et pas de station et que play est a true on le passe a false
		if (
			!observationImagery?.show &&
			!observationImagery?.showStation &&
			observationImagery?.isPlayed
		) {
			observationImagery.setImageryPlayer(false);
		}
	}, [
		observationImagery?.show,
		observationImagery?.showStation,
		observationImagery?.isPlayed,
	]);
};
